import React, { useEffect, useState } from 'react';
import {
  Card, Table, Tag, Button, Space, Input, Select, Modal, Form,
  message, Popconfirm, Row, Col, Statistic, Badge, Tabs, Image, Empty,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  EyeOutlined, BookOutlined, CheckCircleOutlined, CloseCircleOutlined,
  StarOutlined, StarFilled, PictureOutlined,
} from '@ant-design/icons';
import {
  getTrainingCategories,
  getTrainingCategoryDetail,
  createTrainingCategory,
  updateTrainingCategory,
  deleteTrainingCategory,
  getTrainingList,
  getTrainingDetail,
  createTraining,
  updateTraining,
  deleteTraining,
  toggleTrainingPublish,
  toggleTrainingRecommend,
  getImageList,
} from '../services/adminApi';
import { getCurrentApiBaseUrl } from '../services/api';

const { TextArea } = Input;
const { Search } = Input;
const { TabPane } = Tabs;

const buildFullImageUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const baseUrl = getCurrentApiBaseUrl();
  const apiPrefix = '/api/v1';
  const cleanBaseUrl = baseUrl.endsWith(apiPrefix) 
    ? baseUrl.slice(0, -apiPrefix.length) 
    : baseUrl;
  return cleanBaseUrl + (url.startsWith('/') ? '' : '/') + url;
};

const TrainingManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'articles' | 'categories'>('articles');

  // ==================== 分类管理状态 ====================
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryForm] = Form.useForm();

  // ==================== 文章管理状态 ====================
  const [articleList, setArticleList] = useState<any[]>([]);
  const [articleLoading, setArticleLoading] = useState(false);
  const [articlePagination, setArticlePagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [articleFilters, setArticleFilters] = useState<{
    categoryId?: number;
    keyword?: string;
    isPublished?: boolean;
    isRecommended?: boolean;
  }>({});
  const [articleModalVisible, setArticleModalVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState<any>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewArticle, setPreviewArticle] = useState<any>(null);
  const [articleForm] = Form.useForm();
  const [articleStats, setArticleStats] = useState({ total: 0, published: 0, recommended: 0, draft: 0 });

  // ==================== 图片选择器状态 ====================
  const [imagePickerVisible, setImagePickerVisible] = useState(false);
  const [imageList, setImageList] = useState<any[]>([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [imagePagination, setImagePagination] = useState({ current: 1, pageSize: 12, total: 0 });
  const [imageFilters, setImageFilters] = useState<{ category?: string; keyword?: string }>({});
  const [selectedImageField, setSelectedImageField] = useState<string>('coverImage');

  // ==================== 分类管理方法 ====================

  const fetchCategoryList = async () => {
    setCategoryLoading(true);
    try {
      const res = await getTrainingCategories({ pageSize: 100 });
      const data = res.data?.data;
      if (data) {
        setCategoryList(data.list || []);
      }
    } catch (e) {
      message.error('获取分类列表失败');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({ isActive: true, sortOrder: 0 });
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (record: any) => {
    setEditingCategory(record);
    categoryForm.setFieldsValue({
      name: record.name,
      type: record.type,
      icon: record.icon,
      description: record.description,
      sortOrder: record.sortOrder,
      isActive: record.isActive,
    });
    setCategoryModalVisible(true);
  };

  const handleSubmitCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      if (editingCategory) {
        await updateTrainingCategory(editingCategory.id, values);
        message.success('分类更新成功');
      } else {
        await createTrainingCategory(values);
        message.success('分类创建成功');
      }
      setCategoryModalVisible(false);
      fetchCategoryList();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '操作失败');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await deleteTrainingCategory(id);
      message.success('删除成功');
      fetchCategoryList();
    } catch (_) {
      message.error('删除失败，请确保该分类下没有文章');
    }
  };

  // ==================== 文章管理方法 ====================

  const fetchArticleList = async (page = 1, pageSize = 10) => {
    setArticleLoading(true);
    try {
      const params: any = { page, pageSize, ...articleFilters };
      const res = await getTrainingList(params);
      const data = res.data?.data;
      if (data) {
        setArticleList(data.list || []);
        setArticlePagination({ current: data.page, pageSize: data.pageSize, total: data.total });
      }
    } catch (e) {
      message.error('获取培训列表失败');
    } finally {
      setArticleLoading(false);
    }
  };

  const fetchArticleStats = async () => {
    try {
      const [allRes, pubRes, recRes, draftRes] = await Promise.all([
        getTrainingList({ page: 1, pageSize: 1 }),
        getTrainingList({ page: 1, pageSize: 1, isPublished: true }),
        getTrainingList({ page: 1, pageSize: 1, isRecommended: true }),
        getTrainingList({ page: 1, pageSize: 1, isPublished: false }),
      ]);
      setArticleStats({
        total: allRes.data?.data?.total || 0,
        published: pubRes.data?.data?.total || 0,
        recommended: recRes.data?.data?.total || 0,
        draft: draftRes.data?.data?.total || 0,
      });
    } catch (_) {}
  };

  // ==================== 图片选择器方法 ====================

  const fetchImageList = async (page = 1, pageSize = 12) => {
    setImageLoading(true);
    try {
      const params: any = { page, pageSize, isActive: true, ...imageFilters };
      const res = await getImageList(params);
      const data = res.data?.data;
      if (data) {
        setImageList(data.list || []);
        setImagePagination({ current: data.page, pageSize: data.pageSize, total: data.total });
      }
    } catch (e) {
      console.error('获取图片列表失败:', e);
    } finally {
      setImageLoading(false);
    }
  };

  const handleOpenImagePicker = (field: string = 'coverImage') => {
    setSelectedImageField(field);
    setImageFilters({});
    setImagePagination({ current: 1, pageSize: 12, total: 0 });
    fetchImageList(1, 12);
    setImagePickerVisible(true);
  };

  const handleSelectImage = (image: any) => {
    const imageUrl = image.fileUrl;
    articleForm.setFieldsValue({ [selectedImageField]: imageUrl });
    message.success('已选择图片');
    setImagePickerVisible(false);
  };

  useEffect(() => {
    if (activeTab === 'categories') {
      fetchCategoryList();
    } else {
      fetchArticleList();
      fetchArticleStats();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'articles') {
      fetchArticleList();
    }
  }, [articleFilters]);

  useEffect(() => {
    if (imagePickerVisible) {
      fetchImageList();
    }
  }, [imageFilters, imagePickerVisible]);

  const handleCreateArticle = () => {
    if (categoryList.length === 0) {
      Modal.warning({
        title: '请先添加培训分类',
        content: '在发布培训文章之前，请先切换到"培训分类管理"标签页添加至少一个分类。',
        okText: '去添加分类',
        onOk: () => {
          setActiveTab('categories');
        },
      });
      return;
    }
    setEditingArticle(null);
    articleForm.resetFields();
    articleForm.setFieldsValue({ isRecommended: false, isPublished: false });
    setArticleModalVisible(true);
  };

  const handleEditArticle = (record: any) => {
    setEditingArticle(record);
    articleForm.setFieldsValue({
      title: record.title,
      description: record.description,
      content: record.content,
      categoryId: record.categoryId,
      coverImage: record.coverImage,
      videoUrl: record.videoUrl,
      duration: record.duration,
      author: record.author,
      tags: record.tags ? JSON.parse(record.tags) : [],
      isRecommended: record.isRecommended,
      isPublished: record.isPublished,
    });
    setArticleModalVisible(true);
  };

  const handlePreviewArticle = (record: any) => {
    setPreviewArticle(record);
    setPreviewVisible(true);
  };

  const handleSubmitArticle = async () => {
    try {
      const values = await articleForm.validateFields();
      if (editingArticle) {
        await updateTraining(editingArticle.id, values);
        message.success('培训更新成功');
      } else {
        await createTraining(values);
        message.success('培训创建成功');
      }
      setArticleModalVisible(false);
      fetchArticleList(articlePagination.current, articlePagination.pageSize);
      fetchArticleStats();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '操作失败');
    }
  };

  const handleDeleteArticle = async (id: number) => {
    try {
      await deleteTraining(id);
      message.success('删除成功');
      fetchArticleList(articlePagination.current, articlePagination.pageSize);
      fetchArticleStats();
    } catch (_) {
      message.error('删除失败');
    }
  };

  const handleTogglePublish = async (id: number) => {
    try {
      const res = await toggleTrainingPublish(id);
      const statusText = res.data?.data?.isPublished ? '已发布' : '已下架';
      message.success(`培训${statusText}`);
      fetchArticleList(articlePagination.current, articlePagination.pageSize);
      fetchArticleStats();
    } catch (_) {
      message.error('操作失败');
    }
  };

  const handleToggleRecommend = async (id: number) => {
    try {
      const res = await toggleTrainingRecommend(id);
      const statusText = res.data?.data?.isRecommended ? '已推荐' : '已取消推荐';
      message.success(`培训${statusText}`);
      fetchArticleList(articlePagination.current, articlePagination.pageSize);
      fetchArticleStats();
    } catch (_) {
      message.error('操作失败');
    }
  };

  // 分类表格列
  const categoryColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '类型标识',
      dataIndex: 'type',
      key: 'type',
      width: 120,
    },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: (v: string) => v || '-',
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      sorter: (a: any, b: any) => a.sortOrder - b.sortOrder,
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (v: boolean) => (
        v ? <Badge status="success" text="启用" /> : <Badge status="default" text="禁用" />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditCategory(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除这个分类？" onConfirm={() => handleDeleteCategory(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 文章表格列
  const articleColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '封面',
      dataIndex: 'coverImage',
      key: 'coverImage',
      width: 100,
      render: (v: string) => (
        v ? (
          <img src={buildFullImageUrl(v)} alt="封面" style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4 }} />
        ) : (
          <div style={{ width: 60, height: 40, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOutlined style={{ color: '#ccc' }} />
          </div>
        )
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (text: string, record: any) => (
        <a onClick={() => handlePreviewArticle(record)}>{text}</a>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (v: any) => v?.name || '-',
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 80,
    },
    {
      title: '浏览/学习',
      key: 'stats',
      width: 120,
      render: (_: any, record: any) => (
        <div>
          <div>👁️ {record.viewCount || 0}</div>
          <div>👥 {record.learnCount || 0}</div>
        </div>
      ),
    },
    {
      title: '推荐',
      dataIndex: 'isRecommended',
      key: 'isRecommended',
      width: 60,
      render: (v: boolean) => (
        v ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined style={{ color: '#ccc' }} />
      ),
    },
    {
      title: '状态',
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 80,
      render: (v: boolean) => (
        v ? <Badge status="success" text="已发布" /> : <Badge status="default" text="草稿" />
      ),
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_: any, record: any) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handlePreviewArticle(record)}>
            预览
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditArticle(record)}>
            编辑
          </Button>
          <Button
            type="link" size="small"
            icon={record.isRecommended ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
            onClick={() => handleToggleRecommend(record.id)}
          >
            {record.isRecommended ? '取消推荐' : '推荐'}
          </Button>
          <Button
            type="link" size="small"
            icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleTogglePublish(record.id)}
          >
            {record.isPublished ? '下架' : '发布'}
          </Button>
          <Popconfirm title="确定删除这篇培训？" onConfirm={() => handleDeleteArticle(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const categoryOptions = categoryList.map(c => ({ value: c.id, label: c.name }));

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as 'articles' | 'categories')}>
        <TabPane tab="培训文章管理" key="articles">
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size="small">
                <Statistic title="全部培训" value={articleStats.total} prefix={<BookOutlined />} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="已发布" value={articleStats.published} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="已推荐" value={articleStats.recommended} prefix={<StarFilled />} valueStyle={{ color: '#faad14' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="草稿" value={articleStats.draft} prefix={<CloseCircleOutlined />} valueStyle={{ color: '#999' }} />
              </Card>
            </Col>
          </Row>

          <Card
            title="培训文章列表"
            extra={
              <Space>
                <Search
                  placeholder="搜索标题/描述"
                  allowClear
                  style={{ width: 200 }}
                  onSearch={(v) => setArticleFilters((f) => ({ ...f, keyword: v || undefined }))}
                />
                <Select
                  placeholder="分类筛选"
                  allowClear
                  style={{ width: 120 }}
                  onChange={(v) => setArticleFilters((f) => ({ ...f, categoryId: v }))}
                  options={categoryOptions}
                />
                <Select
                  placeholder="发布状态"
                  allowClear
                  style={{ width: 120 }}
                  onChange={(v) => setArticleFilters((f) => ({ ...f, isPublished: v }))}
                  options={[
                    { value: true, label: '已发布' },
                    { value: false, label: '草稿' },
                  ]}
                />
                <Select
                  placeholder="推荐状态"
                  allowClear
                  style={{ width: 120 }}
                  onChange={(v) => setArticleFilters((f) => ({ ...f, isRecommended: v }))}
                  options={[
                    { value: true, label: '已推荐' },
                    { value: false, label: '未推荐' },
                  ]}
                />
                <Button icon={<ReloadOutlined />} onClick={() => { fetchArticleList(); fetchArticleStats(); }}>刷新</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateArticle}>发布培训</Button>
              </Space>
            }
          >
            <Table
              columns={articleColumns}
              dataSource={articleList}
              rowKey="id"
              loading={articleLoading}
              pagination={{
                ...articlePagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => fetchArticleList(page, pageSize),
              }}
              size="middle"
              scroll={{ x: 1400 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="培训分类管理" key="categories">
          <Card
            title="培训分类列表"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={fetchCategoryList}>刷新</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateCategory}>新增分类</Button>
              </Space>
            }
          >
            <Table
              columns={categoryColumns}
              dataSource={categoryList}
              rowKey="id"
              loading={categoryLoading}
              pagination={false}
              size="middle"
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 分类编辑弹窗 */}
      <Modal
        title={editingCategory ? '编辑分类' : '新增分类'}
        open={categoryModalVisible}
        onOk={handleSubmitCategory}
        onCancel={() => setCategoryModalVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item name="name" label="分类名称" rules={[{ required: true, message: '请输入分类名称' }]}>
            <Input placeholder="例如：基础知识" />
          </Form.Item>
          <Form.Item name="type" label="类型标识" rules={[{ required: true, message: '请输入类型标识' }]}>
            <Input placeholder="英文标识，例如：basic（编辑时不可修改）" disabled={!!editingCategory} />
          </Form.Item>
          <Form.Item name="icon" label="图标">
            <Input placeholder="图标emoji或图标名称（可选）" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序">
            <Input type="number" placeholder="数字越小越靠前" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={2} placeholder="分类描述（可选）" />
          </Form.Item>
          <Form.Item name="isActive" label="是否启用" valuePropName="checked">
            <Select
              options={[
                { value: true, label: '启用' },
                { value: false, label: '禁用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 文章编辑弹窗 */}
      <Modal
        title={editingArticle ? '编辑培训' : '发布培训'}
        open={articleModalVisible}
        onOk={handleSubmitArticle}
        onCancel={() => setArticleModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={720}
        destroyOnClose
      >
        <Form form={articleForm} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }, { max: 200, message: '标题不能超过200字' }]}>
            <Input placeholder="请输入培训标题" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="categoryId" 
                label="分类"
                extra={categoryList.length === 0 ? (
                  <span style={{ color: '#faad14' }}>
                    ⚠️ 暂无分类，请先在"培训分类管理"标签页添加分类
                  </span>
                ) : undefined}
              >
                <Select 
                  options={categoryOptions} 
                  placeholder={categoryList.length === 0 ? '暂无分类，请先添加' : '请选择分类（可选）'}
                  allowClear
                  disabled={categoryList.length === 0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="author" label="作者" rules={[{ required: true, message: '请输入作者' }]}>
                <Input placeholder="请输入作者名称" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="duration" label="时长">
                <Input placeholder="例如：30分钟（可选）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tags" label="标签">
                <Select
                  mode="tags"
                  placeholder="输入标签后回车添加（可选）"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="描述" rules={[{ required: true, message: '请输入描述' }]}>
            <TextArea rows={2} placeholder="请输入培训描述" />
          </Form.Item>
          <Form.Item name="content" label="正文">
            <TextArea rows={6} placeholder="请输入培训正文内容（可选）" />
          </Form.Item>
          <Form.Item name="coverImage" label="封面图片">
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="coverImage" noStyle>
                <Input placeholder="请输入图片URL或从图片库选择" />
              </Form.Item>
              <Button type="primary" icon={<PictureOutlined />} onClick={() => handleOpenImagePicker('coverImage')}>
                从图片库选择
              </Button>
            </Space.Compact>
            <Form.Item noStyle dependencies={['coverImage']}>
              {({ getFieldValue }) => {
                const coverImage = getFieldValue('coverImage');
                if (coverImage) {
                  return (
                    <div style={{ marginTop: 8 }}>
                      <Image
                        src={buildFullImageUrl(coverImage)}
                        style={{ maxWidth: 200, maxHeight: 150, borderRadius: 4 }}
                        fallback="https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=placeholder%20image%20book%20cover&image_size=square_hd"
                      />
                    </div>
                  );
                }
                return null;
              }}
            </Form.Item>
          </Form.Item>
          <Form.Item name="videoUrl" label="视频链接">
            <Input placeholder="请输入视频链接（可选）" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isRecommended" label="设为推荐" valuePropName="checked">
                <Select
                  options={[
                    { value: true, label: '是 - 推荐显示' },
                    { value: false, label: '否 - 普通显示' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isPublished" label="立即发布" valuePropName="checked">
                <Select
                  options={[
                    { value: true, label: '是 - 立即发布' },
                    { value: false, label: '否 - 保存为草稿' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 预览弹窗 */}
      <Modal
        title={previewArticle?.title}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={640}
      >
        {previewArticle && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Tag color="green">{previewArticle.category?.name || '未分类'}</Tag>
              <span style={{ color: '#999', marginLeft: 8 }}>
                {previewArticle.author} · {previewArticle.publishedAt ? new Date(previewArticle.publishedAt).toLocaleString('zh-CN') : '未发布'}
              </span>
              {previewArticle.isRecommended && (
                <Tag color="gold" style={{ marginLeft: 8 }}>推荐</Tag>
              )}
              <span style={{ color: '#999', marginLeft: 8 }}>👁️ {previewArticle.viewCount} 👥 {previewArticle.learnCount}人学习</span>
            </div>
            {previewArticle.coverImage && (
              <img src={buildFullImageUrl(previewArticle.coverImage)} alt="封面" style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }} />
            )}
            <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 16 }}>{previewArticle.description}</p>
            {previewArticle.content && (
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{previewArticle.content}</div>
            )}
          </div>
        )}
      </Modal>

      {/* 图片选择器弹窗 */}
      <Modal
        title="从图片库选择"
        open={imagePickerVisible}
        onCancel={() => setImagePickerVisible(false)}
        footer={[
          <Button key="close" onClick={() => setImagePickerVisible(false)}>取消</Button>,
        ]}
        width={800}
        destroyOnClose
      >
        <div>
          <Space style={{ marginBottom: 16 }}>
            <Search
              placeholder="搜索文件名/描述"
              allowClear
              style={{ width: 200 }}
              onSearch={(v) => setImageFilters((f) => ({ ...f, keyword: v || undefined }))}
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchImageList()}>刷新</Button>
          </Space>

          {imageLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
          ) : imageList.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无图片"
            >
              <div style={{ color: '#999', fontSize: 13 }}>
                请先在"图片管理"中上传图片
              </div>
            </Empty>
          ) : (
            <Row gutter={[16, 16]}>
              {imageList.map((image) => (
                <Col span={6} key={image.id}>
                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                    }}
                    onClick={() => handleSelectImage(image)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                      e.currentTarget.style.borderColor = '#1890ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#f0f0f0';
                    }}
                  >
                    <div
                      style={{
                        height: 100,
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {image.fullUrl || image.fileUrl ? (
                        <Image
                          src={buildFullImageUrl(image.fullUrl || image.fileUrl)}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          preview={false}
                          fallback="https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=placeholder%20image%20book%20cover&image_size=square_hd"
                        />
                      ) : (
                        <PictureOutlined style={{ fontSize: 32, color: '#999' }} />
                      )}
                    </div>
                    <div style={{ padding: 8 }}>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#666',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={image.originalName}
                      >
                        {image.originalName}
                      </div>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                        {image.category ? <Tag color="blue" style={{ margin: 0 }}>{image.category}</Tag> : '未分类'}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {imageList.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <span style={{ color: '#999', marginRight: 16 }}>
                共 {imagePagination.total} 张图片
              </span>
              <Button
                disabled={imagePagination.current <= 1}
                onClick={() => {
                  setImagePagination((p) => ({ ...p, current: p.current - 1 }));
                  fetchImageList(imagePagination.current - 1, imagePagination.pageSize);
                }}
              >
                上一页
              </Button>
              <Button
                disabled={imagePagination.current * imagePagination.pageSize >= imagePagination.total}
                onClick={() => {
                  setImagePagination((p) => ({ ...p, current: p.current + 1 }));
                  fetchImageList(imagePagination.current + 1, imagePagination.pageSize);
                }}
                style={{ marginLeft: 8 }}
              >
                下一页
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TrainingManagement;
